/**
 * Deploy en caliente: que publicar no le rompa la sesión a nadie.
 *
 * Una SPA con code splitting carga sus pantallas bajo demanda: el `index.html`
 * que tiene el navegador apunta a archivos con un hash (`LandingPage-abc123.js`).
 * Cuando publicamos, esos archivos se reemplazan por otros con hash nuevo. Quien
 * ya estaba adentro sigue con el index viejo en memoria, así que al abrir la
 * siguiente pantalla pide un archivo que ya no existe: el import falla y la app
 * se cae. El síntoma es el clásico "se rompió, recargué y funcionó".
 *
 * Pasó de verdad el 1 ago 2026, 21:27–21:30: tres caídas seguidas mientras
 * Netlify publicaba, con `LandingPage` fallando bajo dos hashes distintos en un
 * minuto. Este archivo cubre los dos lados:
 *
 *   - `recargarPorDeploy()` — reacción. Si un import ya falló Y confirmamos que
 *     el servidor tiene otra versión, recargamos con un cartel que lo explica.
 *   - `vigilarVersion()` — prevención. Detecta la versión nueva ANTES de que el
 *     usuario choque con ella y le ofrece actualizar cuando le quede bien.
 *     Si la pestaña estuvo un buen rato en segundo plano (o el equipo dormido)
 *     y no hay nada escrito en pantalla, recarga sola al volver: quien retoma
 *     una pestaña de hace días no tiene por qué ver la app fallar (29-30 oct
 *     2026: pestañas abiertas desde antes de un cambio de permisos seguían
 *     pidiendo datos con el código viejo y la home les mostraba vacía).
 *
 * La regla que gobierna todo el archivo: NUNCA recargar por sospecha. Un import
 * también falla sin conexión, con un portal cautivo de wifi, con un proxy
 * corporativo o con un 5xx del CDN — y ahí recargar es lo peor que podemos
 * hacer, porque tira la app y todo lo que el usuario tuviera escrito. Solo
 * recargamos cuando comparamos versiones y son distintas.
 */
import { track } from '@/lib/analytics';

const CLAVE_RECARGA = 'gurumba:recarga-por-deploy';
/** Dos recargas separadas por menos de esto son un bucle, no una solución. */
const VENTANA_ANTI_BUCLE_MS = 30_000;
/** Si dos recargas no lo arreglaron, no lo va a arreglar la tercera. */
const MAX_INTENTOS = 2;

interface EstadoRecarga { ts: number; intentos: number }

function leerEstado(): EstadoRecarga | null {
  try {
    const crudo = window.sessionStorage.getItem(CLAVE_RECARGA);
    if (!crudo) return { ts: 0, intentos: 0 };
    const v = JSON.parse(crudo) as Partial<EstadoRecarga>;
    return { ts: Number(v.ts) || 0, intentos: Number(v.intentos) || 0 };
  } catch {
    // Safari con almacenamiento bloqueado: sin memoria no podemos garantizar
    // que esto no entre en bucle, así que mejor no recargar nunca.
    return null;
  }
}

function guardarEstado(e: EstadoRecarga): boolean {
  try {
    window.sessionStorage.setItem(CLAVE_RECARGA, JSON.stringify(e));
    return true;
  } catch {
    return false;
  }
}

/**
 * ¿Este error es "no pude traer el módulo" y no un bug nuestro?
 *
 * Cada navegador lo redacta a su manera. Que dé `true` NO significa que sea un
 * deploy: significa que vale la pena ir a comprobarlo.
 */
export function esErrorDeChunk(err: unknown): boolean {
  const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||  // Chrome, Edge
    /error loading dynamically imported module/i.test(msg) ||    // Firefox
    /Importing a module script failed/i.test(msg) ||             // Safari
    /Unable to preload CSS/i.test(msg) ||                        // Vite
    /ChunkLoadError/i.test(msg)
  );
}

/**
 * Amnistía del contador cuando la app demuestra estar sana.
 *
 * Sin esto el contador era de por vida en la pestaña: dos deploys legítimos
 * en un día (cada uno con su recarga silenciosa exitosa) agotaban
 * MAX_INTENTOS, y el TERCER deploy — horas después, sin ningún bucle — le
 * mostraba al usuario la pantalla de error por un chunk vencido que una
 * recarga habría resuelto. Visto en producción el 2026-08-26.
 *
 * La regla: si la app lleva 20 segundos corriendo sin morir, la última
 * recarga funcionó y el historial ya no es evidencia de un bucle. Se limpia.
 * El bucle real sigue cubierto: en un bucle la app muere antes de los 20s y
 * este timer nunca llega a disparar.
 */
export function marcarArranqueSano(): void {
  window.setTimeout(() => {
    try {
      window.sessionStorage.removeItem(CLAVE_RECARGA);
    } catch {
      // Sin storage no hay contador que limpiar.
    }
  }, 20_000);
}

// =====================================================================
// Huella de versión
// =====================================================================

/** Normaliza a ruta para que las dos huellas se comparen igual. */
function comoRuta(src: string): string {
  try {
    return new URL(src, window.location.origin).pathname;
  } catch {
    return src;
  }
}

/**
 * El script de entrada que está corriendo ahora. Lleva el hash del build, así
 * que sirve de huella de la versión sin generar ningún archivo extra ni tocar
 * la configuración del build. En `vite dev` no hay assets con hash y devuelve
 * null: ahí no hay nada que vigilar.
 */
export function huellaEnMemoria(): string | null {
  const scripts = Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]'),
  );
  const entrada = scripts.find((s) => s.src.includes('/assets/'));
  return entrada ? comoRuta(entrada.src) : null;
}

/** La misma huella, pero según el `index.html` que el servidor sirve AHORA. */
async function huellaPublicada(): Promise<string | null> {
  const res = await fetch('/index.html', { cache: 'no-store' });
  if (!res.ok) return null;
  const html = await res.text();
  const m = html.match(/<script[^>]+type="module"[^>]+src="([^"]*\/assets\/[^"]+)"/);
  return m?.[1] ? comoRuta(m[1]) : null;
}

/**
 * ¿El servidor tiene una versión distinta de la que corre acá?
 *
 * `null` = no se pudo saber (sin red, fetch fallido, dev). Quien pregunta debe
 * tratar el `null` como "no sé", nunca como "sí".
 */
async function hayVersionNueva(): Promise<string | null> {
  const actual = huellaEnMemoria();
  if (!actual) return null;
  try {
    const publicada = await huellaPublicada();
    if (!publicada || publicada === actual) return null;
    return publicada;
  } catch {
    return null;
  }
}

// =====================================================================
// Reacción: recargar cuando ya se rompió
// =====================================================================

/** Cartel de "estamos actualizando", en HTML plano. */
function mostrarCartelDeActualizacion(): void {
  if (document.getElementById('gurumba-actualizando')) return;

  // Estilos en línea a propósito: si el chunk de JS no cargó, el CSS del deploy
  // viejo puede haber corrido la misma suerte. Esto tiene que verse igual
  // aunque no quede una sola hoja de estilos en pie.
  const capa = document.createElement('div');
  capa.id = 'gurumba-actualizando';
  capa.setAttribute('role', 'status');
  capa.setAttribute('aria-live', 'polite');
  capa.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:2147483647',
    'display:flex', 'flex-direction:column', 'align-items:center', 'justify-content:center',
    'gap:18px', 'background:#0B1A1F', 'color:#fff',
    'font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif',
    'text-align:center', 'padding:24px',
  ].join(';');

  const estilo = document.createElement('style');
  estilo.textContent = '@keyframes gurumba-giro{to{transform:rotate(360deg)}}';

  const spinner = document.createElement('div');
  spinner.style.cssText = [
    'width:34px', 'height:34px', 'border-radius:9999px',
    'border:3px solid rgba(255,255,255,.18)', 'border-top-color:#A8BF3C',
    'animation:gurumba-giro .8s linear infinite',
  ].join(';');

  const titulo = document.createElement('p');
  titulo.textContent = 'Actualizando Gurumba…';
  titulo.style.cssText = 'margin:0;font-size:16px;font-weight:600';

  const detalle = document.createElement('p');
  detalle.textContent = 'Publicamos una versión nueva. Esto toma un segundo.';
  detalle.style.cssText = 'margin:0;font-size:13px;color:rgba(255,255,255,.6);max-width:320px';

  // Salida manual. Si la recarga automática no llega a ocurrir (una pestaña en
  // segundo plano puede tener los timers frenados), este cartel sin botón sería
  // una pantalla sin salida, tapando todo con z-index máximo.
  const boton = document.createElement('button');
  boton.type = 'button';
  boton.textContent = 'Recargar ahora';
  boton.style.cssText = [
    'margin-top:6px', 'padding:10px 22px', 'border:0', 'border-radius:9999px',
    'background:#A8BF3C', 'color:#071114', 'font:inherit', 'font-weight:600',
    'font-size:14px', 'cursor:pointer',
  ].join(';');
  boton.onclick = () => window.location.reload();

  capa.append(estilo, spinner, titulo, detalle, boton);
  document.body.appendChild(capa);
}

/**
 * Recarga si —y solo si— el fallo se explica por un deploy nuevo.
 *
 * Devuelve `false` cuando NO va a recargar; en ese caso quien llama debe dejar
 * que el error siga su curso para que lo vea el ErrorBoundary. Es lo correcto:
 * si no hubo deploy, el problema es nuestro y hay que enterarse.
 */
export async function recargarPorDeploy(): Promise<boolean> {
  // Sin conexión el import falla igual, y recargar mostraría el dinosaurio del
  // navegador en vez de nuestra pantalla de error. Nunca recargar offline.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;

  const estado = leerEstado();
  if (!estado) return false;
  if (estado.intentos >= MAX_INTENTOS) return false;
  if (estado.ts && Date.now() - estado.ts < VENTANA_ANTI_BUCLE_MS) return false;

  // La comprobación que evita el falso positivo: si el servidor sigue sirviendo
  // la misma versión, esto no fue un deploy y recargar no arregla nada.
  const nueva = await hayVersionNueva();
  if (!nueva) return false;

  if (!guardarEstado({ ts: Date.now(), intentos: estado.intentos + 1 })) return false;

  // Registrar ANTES de irnos. Sin esto perderíamos de vista justo el evento que
  // este archivo existe para resolver, y no sabríamos si funciona.
  try {
    track('app_error', {
      tipo: 'recarga_por_deploy',
      message: `chunk vencido; version publicada ${nueva}`,
      path: window.location.pathname,
    });
  } catch {
    // Analytics nunca puede impedir la recarga.
  }

  mostrarCartelDeActualizacion();
  // Un respiro para que el cartel se pinte y el evento salga.
  window.setTimeout(() => window.location.reload(), 400);
  return true;
}

/**
 * Recarga porque hay una versión nueva y la pestaña se está retomando.
 *
 * Mismas guardas que `recargarPorDeploy` (sin conexión no, anti-bucle, tope de
 * intentos). La diferencia: acá ya sabemos que hay versión nueva (`nueva`) y
 * nada se rompió todavía; solo evitamos que se rompa.
 */
async function recargarPorVersionNueva(nueva: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;

  const estado = leerEstado();
  if (!estado) return false;
  if (estado.intentos >= MAX_INTENTOS) return false;
  if (estado.ts && Date.now() - estado.ts < VENTANA_ANTI_BUCLE_MS) return false;
  if (!guardarEstado({ ts: Date.now(), intentos: estado.intentos + 1 })) return false;

  try {
    track('app_error', {
      tipo: 'recarga_por_version_nueva',
      message: `pestaña retomada con version vieja; version publicada ${nueva}`,
      path: window.location.pathname,
    });
  } catch {
    // Analytics nunca puede impedir la recarga.
  }

  mostrarCartelDeActualizacion();
  window.setTimeout(() => window.location.reload(), 400);
  return true;
}

/**
 * ¿Hay algo escrito en pantalla que una recarga borraría?
 *
 * Conservador a propósito: cualquier campo de texto visible con contenido (un
 * comentario a medias, un buscador, un formulario) cuenta. Ante la duda —o si
 * la comprobación falla— decimos que sí, y entonces solo se avisa, no se recarga.
 */
function hayTextoSinGuardar(): boolean {
  try {
    const campos = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      'textarea, input:not([type]), input[type="text"], input[type="email"], input[type="tel"], ' +
        'input[type="url"], input[type="number"], input[type="search"], input[type="password"]',
    );
    for (const campo of Array.from(campos)) {
      if (campo.disabled || campo.readOnly) continue;
      const visible = campo.offsetParent !== null || campo.getClientRects().length > 0;
      if (visible && campo.value.trim() !== '') return true;
    }
    const editables = document.querySelectorAll<HTMLElement>('[contenteditable=""], [contenteditable="true"]');
    for (const el of Array.from(editables)) {
      if ((el.textContent ?? '').trim() !== '') return true;
    }
    return false;
  } catch {
    return true;
  }
}

// =====================================================================
// Prevención: avisar antes de que se rompa
// =====================================================================

/**
 * Pausa mínima (pestaña oculta o equipo dormido) para recargar sola al volver.
 * Por debajo de esto se asume que el usuario está en medio de algo y solo se
 * avisa.
 */
const RECARGA_SOLA_TRAS_PAUSA_MS = 10 * 60_000;

/**
 * Avisa cuando el servidor ya tiene una versión distinta de la que corre acá.
 *
 * Comprueba al arrancar, cada `intervaloMs`, y cada vez que la pestaña vuelve a
 * primer plano — que es cuando alguien retoma el trabajo después de un rato y
 * es más probable que se haya publicado algo entretanto.
 *
 * `alDetectar` recibe `permitirReaviso`: si el usuario descarta el aviso sin
 * querer (en móvil un swipe lo cierra), llamarla hace que se le vuelva a avisar
 * en la siguiente comprobación. Sin eso, un swipe accidental lo condenaría a
 * quedarse en la versión vieja hasta chocar con ella.
 *
 * Devuelve la función para dejar de vigilar.
 */
export function vigilarVersion(
  alDetectar: (permitirReaviso: () => void) => void,
  intervaloMs = 5 * 60_000,
): () => void {
  // En desarrollo no hay assets con hash y no hay nada que vigilar.
  if (!huellaEnMemoria()) return () => {};

  let detenido = false;
  let avisada: string | null = null;
  // Cuándo se ocultó la pestaña (null = visible) y cuándo corrió el último
  // tick del intervalo: un salto grande entre ticks es un equipo que durmió
  // con la pestaña al frente (ahí no siempre llega 'visibilitychange').
  let ocultaDesde: number | null = document.visibilityState === 'hidden' ? Date.now() : null;
  let ultimoTick = Date.now();

  async function revisar(retomando = false) {
    if (detenido || document.visibilityState !== 'visible') return;
    const nueva = await hayVersionNueva();
    if (!nueva) return;
    // Quien retoma tras una pausa larga y sin nada escrito: recargar sola.
    if (retomando && !hayTextoSinGuardar() && (await recargarPorVersionNueva(nueva))) return;
    if (nueva === avisada) return;
    avisada = nueva;
    alDetectar(() => { avisada = null; });
  }

  const id = window.setInterval(() => {
    const ahora = Date.now();
    const durmio = ahora - ultimoTick >= Math.max(RECARGA_SOLA_TRAS_PAUSA_MS, intervaloMs * 2);
    ultimoTick = ahora;
    void revisar(durmio);
  }, intervaloMs);
  const alCambiarVisibilidad = () => {
    if (document.visibilityState === 'hidden') {
      ocultaDesde = Date.now();
      return;
    }
    const pausaLarga = ocultaDesde !== null && Date.now() - ocultaDesde >= RECARGA_SOLA_TRAS_PAUSA_MS;
    ocultaDesde = null;
    void revisar(pausaLarga);
  };
  document.addEventListener('visibilitychange', alCambiarVisibilidad);
  void revisar();

  return () => {
    detenido = true;
    window.clearInterval(id);
    document.removeEventListener('visibilitychange', alCambiarVisibilidad);
  };
}
